import { Controller } from "@hotwired/stimulus"
import fitty from "fitty"

export default class extends Controller {
  static targets = [
    "oneLarge",
    "twoMedium",
    "fourSmall",
    "printForm",
    "preview",
    "previewCover",
    "cardTitle",
    "cardGrid",
    "freeSpaceCheckbox",
    "freeSpaceInput",
    "freeSpaceOutput",
    "cellTextOutput",
    "printButton",
    "downloadButton",
    "lockOverlay",
    "phrasesInput",
    "step1",
    "step2",
    "backToStep1Button"
  ]

  // Add constants
  static values = {
    minFontSize: { type: Number, default: 8 },
    maxFontSize: { type: Number, default: 136 },
    stepSize: { type: Number, default: 1 },
    centerCellIndex: { type: Number, default: 12 }
  }

  connect() {
    this.printButtonTimeout = null
    this.phrases = []
    this.freeSpaceText = "✰"
    this.hasFreeSpace = true
    this.fitTitle()

    if (this.phrasesInputTarget.value.length > 0) {
      this.setPhrases({ target: this.phrasesInputTarget })
    }
  }

  setCardsPerPage(event) {
    const cardsPerPage = parseInt(event.target.dataset.value, 10)
    const targetMap = {
      1: [this.oneLargeTarget, this.twoMediumTarget, this.fourSmallTarget],
      2: [this.twoMediumTarget, this.oneLargeTarget, this.fourSmallTarget],
      4: [this.fourSmallTarget, this.oneLargeTarget, this.twoMediumTarget]
    }

    if (targetMap[cardsPerPage]) {
      const [showTarget, ...hideTargets] = targetMap[cardsPerPage]
      showTarget.classList.remove("is-hidden")
      hideTargets.forEach(target => target.classList.add("is-hidden"))
    }

    this.previewCoverTarget.classList.remove("card-preview__cover--hidden")
    this.debounce(() => {
      this.fitTitle()
      this.fitCellTexts()
      
      // Add delay before hiding the preview cover
      setTimeout(() => {
        this.previewCoverTarget.classList.add("card-preview__cover--hidden")
      }, 2000) // 2 seconds delay
    }, 100)
    
    this.setActiveButton(event.target)
    this.updatePrintFormValue('cards_per_page', cardsPerPage)
    this.resetPrintButton()
  }

  setNumberOfCards(event) {
    const numberOfCards = parseInt(event.target.dataset.value, 10)

    this.setActiveButton(event.target)
    this.updatePrintFormValue('number_of_cards', numberOfCards)
    this.resetPrintButton()
  }

  setTitle(event) {
    const title = event.target.value

    for (const cardTitle of this.cardTitleTargets) {
      cardTitle.textContent = title
    }

    this.fitTitle()
    this.updatePrintFormValue('title', title)
    this.resetPrintButton()
  }

  setPhrases(event) {
    try {
      const phrases = event.target.value
        .split("\n")
        .map(phrase => phrase.trim())
        .filter(phrase => phrase.length > 0)

      if (phrases.some(phrase => phrase.length > 100)) { // Add reasonable max length
        console.warn('Some phrases may be too long to display properly')
      }

      this.phrases = phrases
      this.debounce(() => this.fitCellTexts(), 100)
      this.updatePrintFormValue('phrases', event.target.value)
    } catch (error) {
      console.error('Error processing phrases:', error)
    }

    this.resetPrintButton()
  }

  toggleFreeSpace() {
    this.hasFreeSpace = !this.hasFreeSpace

    // const freeSpaceCheckbox = this.freeSpaceCheckboxTarget
    const freeSpaceInput = this.freeSpaceInputTarget

    // Toggle freeSpaceInput visibility
    freeSpaceInput.classList.toggle("is-hidden")

    // Update the print form value
    this.updatePrintFormValue('free_space', this.hasFreeSpace)

    // Fit the cell texts
    this.fitCellTexts()

    this.resetPrintButton()
  }

  setFreeSpaceText() {
    const freeSpaceInput = this.freeSpaceInputTarget

    this.freeSpaceText = freeSpaceInput.value
    this.updatePrintFormValue('free_space_text', this.freeSpaceText)

    // Fit the cell texts
    this.fitCellTexts()
    this.resetPrintButton()
  }

  printCards(event) {
    event.preventDefault()

    // Disable the print button
    this.printButtonTarget.disabled = true

    // Show the lock overlay
    this.lockOverlayTarget.classList.remove("is-hidden")

    // Change the text to "Please wait..."
    this.printButtonTarget.textContent = "Please wait..."

    // Wait for 5 seconds
    this.printButtonTimeout = setTimeout(() => {
      this.step1Target.classList.add("is-hidden")
      this.step2Target.classList.remove("is-hidden")

      // Hide the lock overlay
      this.lockOverlayTarget.classList.add("is-hidden")
    }, 5000)
  }

  backToStep1(event) {
    event.preventDefault()

    this.step1Target.classList.remove("is-hidden")
    this.step2Target.classList.add("is-hidden")
    this.resetPrintButton()
  }
  
  // Private methods
  // ---------------------------------------------------------------------------

  resetPrintButton() {
    this.printButtonTarget.disabled = false
    this.printButtonTarget.textContent = "Print my free bingo cards"
    this.printButtonTarget.classList.remove("is-hidden")

    // Clear the timeout
    if (this.printButtonTimeout) {
      clearTimeout(this.printButtonTimeout)
      this.printButtonTimeout = null
    }
  }

  fitTitle() {
    fitty(".custom-card__title-text", {
      minSize: 36,
      maxSize: 136,
      multiLine: true
    })
  }

  fitCellTexts() {
    for (const cardGrid of this.cardGridTargets) {
      const spans = cardGrid.querySelectorAll("div.custom-card__cell-text > span")
      
      spans.forEach((span, index) => {
        // Handle free space cell
        if (index === 12 && this.hasFreeSpace) {
          span.textContent = this.freeSpaceText
          this.resizeToFit(span)
          return
        }

        // Calculate adjusted phrase index
        const phraseIndex = this.hasFreeSpace && index > 12 ? index - 1 : index

        if (this.phrases[phraseIndex]) {
          span.textContent = this.phrases[phraseIndex]
          this.resizeToFit(span)
        } else {
          span.textContent = ""
        }
      })
    }
  }

  setActiveButton(button) {
    const siblings = button.closest(".buttons").querySelectorAll(".button")
    
    siblings.forEach(sibling => {
      sibling.classList.remove("is-dark")
    })

    button.classList.add("is-dark")
  }

  // Form to print the cards
  updatePrintFormValue(fieldName, value) {
    this.printFormTarget[fieldName].value = value
  }

  // Add debounce helper
  debounce(func, wait) {
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(func, wait)
  }

  // Optimize resizeToFit with binary search
  resizeToFit(element) {
    if (!element) return;
    
    const parent = element.parentElement
    let min = this.minFontSizeValue
    let max = this.maxFontSizeValue
    
    while (min <= max) {
      const mid = Math.floor((min + max) / 2)
      element.style.fontSize = `${mid}px`
      
      if (element.scrollHeight <= parent.clientHeight && 
          element.scrollWidth <= parent.clientWidth) {
        min = mid + 1
      } else {
        max = mid - 1
      }
    }
    
    element.style.fontSize = `${max}px`
  }
}
